import { useEffect, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { Loader2 } from 'lucide-react';
import { saveHangarApplication } from '@/services/deals.service.js';

// "Edit submission" — broker-editable form over the hangar APPLICATION data
// (what the applicant requested), distinct from the §9 Quoted-terms sheet
// (what the carrier quoted). Seeded from the EFFECTIVE application (intake +
// any prior overrides); on save the server diffs against the pristine intake
// and stores only the divergences at submission_workflow.application_overrides,
// then regenerates the packet. Lets the broker correct/complete a submission
// post-intake (e.g. Cirrusazflmi's legacy $1M liability → the 1000/2000 tier).

const ENTITY_TYPES = ['Individual', 'Corporation', 'Partnership', 'LLC', 'Other'];
const CONSTRUCTION_OPTS = ['Frame', 'Joisted Masonry', 'Non-Combustible', 'Masonry Non-Combustible', 'Modified Fire Resistive', 'Fire Resistive', 'Steel', 'Mixed'];
const ALARM_OPTS = ['None', 'Local', 'Central Station Burglar', 'Central Station Fire', 'Central Station Burglar & Fire', '24/7 Monitored', 'Other'];
const UPDATE_TRADES = [['wiring', 'Wiring'], ['plumbing', 'Plumbing'], ['roofing', 'Roofing'], ['heating', 'Heating']];
const COVERAGE_KEYS = [
  ['building_value', 'Building'],
  ['business_personal_property', 'Business Personal Property'],
  ['personal_property_of_others', 'Personal Property of Others'],
  ['business_income', 'Business Income'],
  ['extra_expense', 'Extra Expense'],
  ['misc_tools', 'Miscellaneous Tools'],
  ['employee_tools', 'Employee Tools'],
  ['mobile_equipment', 'Mobile Equipment'],
  ['flood', 'Flood'],
];
const LIABILITY_TIERS = [
  ['500/1000', '500 / 1000'],
  ['1000/1000', '1000 / 1000'],
  ['1000/2000', '1000 / 2000'],
  ['other', 'Other'],
];
const RISK_FIELDS = [
  ['premises_liability', 'Premises liability'],
  ['fuel_tanks_above_ground', 'Above-ground fuel tanks'],
  ['painting_in_hangar', 'Painting in hangar'],
  ['airport_fenced', 'Airport fenced'],
  ['fire_dept_on_premises', 'Fire dept on premises'],
  ['management_on_premises', 'Management on premises'],
];
// Operation type (Type of Risk) — value is the snake_case key the generator
// (hangar-app-pdf.js OPERATION_TYPE_LABELS) renders. 'other' reveals a free-text
// description (e.g. "Lessor's Risk") that riskTypeLabel prints verbatim.
const OPERATION_TYPES = [
  ['fbo', 'FBO'],
  ['mro', 'MRO'],
  ['airline', 'Airline'],
  ['manufacturer', 'Manufacturer'],
  ['corporate_hangar', 'Corporate Hangar'],
  ['tour_operator', 'Tour Operator'],
  ['ambulance_operator', 'Ambulance Operator'],
  ['private_use_hangar', 'Private Use Hangar'],
  ['maintenance_repair', 'Maintenance/Repair Facility'],
  ['parts_distributor', 'Parts Distributor'],
  ['ag_operator', 'Ag Operator'],
  ['helicopter_operator', 'Helicopter Operator'],
  ['airport_authority', 'Airport Authority/Private Airport'],
  ['aviation_school', 'Aviation School'],
  ['other', 'Other'],
];

function seedState(application) {
  const a = application && typeof application === 'object' ? application : {};
  const app = a.applicant || {};
  const ops = a.operations || {};
  const liab = a.liability || {};
  const hist = a.history || {};
  return {
    applicant: {
      insured_name: app.insured_name || '',
      contact_name: app.contact_name || '',
      email: app.email || '',
      phone: app.phone || '',
      entity_type: app.entity_type || '',
      fein: app.fein || '',
    },
    operations: {
      proposed_effective_date: ops.proposed_effective_date || '',
      expiration_date: ops.expiration_date || '',
      type: ops.type || '',
      other_description: ops.other_description || '',
    },
    liability: {
      tier: liab.tier || '',
      other_per_occurrence: liab.other_per_occurrence || '',
      other_aggregate: liab.other_aggregate || '',
    },
    history: {
      prior_coverage: hist.prior_coverage || '',
      losses_5yr: hist.losses_5yr || '',
      losses_detail: hist.losses_detail || '',
      bankruptcies: hist.bankruptcies || '',
    },
    comments: a.comments || '',
    locations: (Array.isArray(a.locations) ? a.locations : []).map(seedLocation),
  };
}

// One location in the sheet's editable shape. seedLocation() with no argument
// mints the empty row behind "Add location" (broker-added building; persists
// as overlay added_locations, the client intake stays frozen).
function seedLocation(l) {
  return {
    airport: l?.airport || '',
    airport_name: l?.airport_name || '',
    address: {
      street: l?.address?.street || '',
      street2: l?.address?.street2 || '',
      city: l?.address?.city || '',
      state: l?.address?.state || '',
      zip: l?.address?.zip || '',
    },
    building: {
      year_built: l?.building?.year_built || '',
      construction: l?.building?.construction || '',
      area_sqft: l?.building?.area_sqft || '',
      sprinklered: l?.building?.sprinklered || '',
      alarm_system: l?.building?.alarm_system || '',
      protection_class: l?.building?.protection_class || '',
      occupancy: l?.building?.occupancy || '',
      updates: {
        wiring: l?.building?.updates?.wiring || '',
        plumbing: l?.building?.updates?.plumbing || '',
        roofing: l?.building?.updates?.roofing || '',
        heating: l?.building?.updates?.heating || '',
      },
    },
    risk: {
      premises_liability: l?.risk?.premises_liability || '',
      fuel_tanks_above_ground: l?.risk?.fuel_tanks_above_ground || '',
      painting_in_hangar: l?.risk?.painting_in_hangar || '',
      airport_fenced: l?.risk?.airport_fenced || '',
      fire_dept_on_premises: l?.risk?.fire_dept_on_premises || '',
      management_on_premises: l?.risk?.management_on_premises || '',
    },
    coverages: Object.fromEntries(
      [...COVERAGE_KEYS.map(([k]) => k), 'misc_coverage_description', 'misc_coverage_limit']
        .map((k) => [k, l?.coverages?.[k] != null && l.coverages[k] !== false ? String(l.coverages[k]) : ''])
    ),
  };
}

function SectionTitle({ children }) {
  return <h3 className="text-[11px] font-semibold uppercase tracking-wide text-text-dim">{children}</h3>;
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] text-text-dim">{label}</span>
      {children}
    </label>
  );
}

function NativeSelect({ value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={onChange}
      className="h-10 w-full rounded-card border border-border bg-surface px-3 text-sm text-text focus:outline-none focus:ring-2 focus:ring-accent/40"
    >
      <option value="">Select…</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

// Three-state Yes/No toggle (click the active one to unset).
function YesNoButtons({ value, onChange }) {
  return (
    <div className="flex gap-1.5">
      {['Yes', 'No'].map((opt) => (
        <Button
          key={opt}
          type="button"
          size="sm"
          variant={value === opt ? 'default' : 'outline'}
          onClick={() => onChange(value === opt ? '' : opt)}
        >
          {opt}
        </Button>
      ))}
    </div>
  );
}

export default function HangarApplicationSheet({ dealId, open, onOpenChange, application, onSaved }) {
  const [form, setForm] = useState(() => seedState(application));
  const [saving, setSaving] = useState(false);

  // Re-seed from the effective application every open so Cancel discards edits.
  useEffect(() => {
    if (open) setForm(seedState(application));
  }, [open, application]);

  const setApplicant = (k) => (e) => setForm((f) => ({ ...f, applicant: { ...f.applicant, [k]: e.target.value } }));
  const setOps = (k) => (e) => setForm((f) => ({ ...f, operations: { ...f.operations, [k]: e.target.value } }));
  const setHistory = (k, v) => setForm((f) => ({ ...f, history: { ...f.history, [k]: v } }));
  const setLoc = (i, patch) => setForm((f) => ({
    ...f,
    locations: f.locations.map((l, j) => (j === i ? { ...l, ...patch } : l)),
  }));
  const setLocNested = (i, section, patch) => setForm((f) => ({
    ...f,
    locations: f.locations.map((l, j) => (j === i ? { ...l, [section]: { ...l[section], ...patch } } : l)),
  }));

  // Client intake locations are frozen (edit-only); rows at/after this index
  // are broker-added and removable. Older payloads without the count freeze
  // everything currently on file.
  const intakeCount = application?.intake_location_count ?? (application?.locations?.length ?? 0);
  const addLocation = () => setForm((f) => ({
    ...f,
    // Same airport as the schedule's first location by default — the common
    // case is another building on the same field.
    locations: [...f.locations, seedLocation({
      airport: f.locations[0]?.airport || '',
      airport_name: f.locations[0]?.airport_name || '',
    })],
  }));
  const removeLocation = (i) => {
    if (i < intakeCount) return;
    setForm((f) => ({ ...f, locations: f.locations.filter((_, j) => j !== i) }));
  };

  function handleSave() {
    if (!dealId || saving) return;
    setSaving(true);
    saveHangarApplication(dealId, form)
      .then((res) => {
        if (!res?.success) {
          toast.error(res?.error || 'Save failed');
          return;
        }
        if (res.link) toast.success('Submission updated — packet regenerated');
        else (toast.warning || toast.error)(`Saved, but regenerate failed: ${res.regen_error || 'unknown'}`);
        for (const w of res.warnings || []) toast.info(w);
        onSaved?.(res);
        onOpenChange(false);
      })
      .catch((err) => toast.error(err?.body?.error || err?.message || 'Save failed'))
      .finally(() => setSaving(false));
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-y-auto sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>Edit submission</SheetTitle>
          <SheetDescription>
            Correct or complete the application data. The client&apos;s original intake is
            preserved; your edits layer on top and the packet regenerates on save.
            Carrier-quoted terms live in the separate Quoted terms sheet.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-6 py-4">
          {/* Applicant */}
          <div className="space-y-2">
            <SectionTitle>Applicant</SectionTitle>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Named insured"><Input value={form.applicant.insured_name} onChange={setApplicant('insured_name')} /></Field>
              <Field label="Contact person"><Input value={form.applicant.contact_name} onChange={setApplicant('contact_name')} /></Field>
              <Field label="Email"><Input type="email" value={form.applicant.email} onChange={setApplicant('email')} /></Field>
              <Field label="Phone"><Input value={form.applicant.phone} onChange={setApplicant('phone')} /></Field>
              <Field label="Entity type">
                <NativeSelect value={form.applicant.entity_type} onChange={setApplicant('entity_type')} options={ENTITY_TYPES} />
              </Field>
              <Field label="FEIN"><Input value={form.applicant.fein} onChange={setApplicant('fein')} /></Field>
              <Field label="Effective date"><Input type="date" value={form.operations.proposed_effective_date} onChange={setOps('proposed_effective_date')} /></Field>
              <Field label="Expiration date (blank = effective + 1 yr)"><Input type="date" value={form.operations.expiration_date} onChange={setOps('expiration_date')} /></Field>
              <Field label="Type of risk">
                <select
                  value={form.operations.type}
                  onChange={setOps('type')}
                  className="h-10 w-full rounded-card border border-border bg-surface px-3 text-sm text-text focus:outline-none focus:ring-2 focus:ring-accent/40"
                >
                  <option value="">Select…</option>
                  {OPERATION_TYPES.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                </select>
              </Field>
              {form.operations.type === 'other' && (
                <Field label="Describe risk type"><Input value={form.operations.other_description} onChange={setOps('other_description')} placeholder="e.g. Lessor's Risk" /></Field>
              )}
            </div>
          </div>

          {/* Locations */}
          {form.locations.map((loc, i) => (
            <div key={i} className="space-y-3 rounded-card border border-border p-3">
              <div className="flex items-center justify-between">
                <SectionTitle>
                  {form.locations.length > 1 ? `Location ${i + 1}` : 'Location'}
                  {i >= intakeCount ? ' · broker added' : ''}
                </SectionTitle>
                {i >= intakeCount && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeLocation(i)}>
                    Remove
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Airport ID"><Input value={loc.airport} onChange={(e) => setLoc(i, { airport: e.target.value.toUpperCase() })} /></Field>
                <Field label="Airport name"><Input value={loc.airport_name} onChange={(e) => setLoc(i, { airport_name: e.target.value })} /></Field>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Street"><Input value={loc.address.street} onChange={(e) => setLocNested(i, 'address', { street: e.target.value })} /></Field>
                <Field label="Suite / Hangar #"><Input value={loc.address.street2} onChange={(e) => setLocNested(i, 'address', { street2: e.target.value })} /></Field>
                <Field label="City"><Input value={loc.address.city} onChange={(e) => setLocNested(i, 'address', { city: e.target.value })} /></Field>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="State"><Input maxLength={2} value={loc.address.state} onChange={(e) => setLocNested(i, 'address', { state: e.target.value.toUpperCase() })} /></Field>
                  <Field label="Zip"><Input value={loc.address.zip} onChange={(e) => setLocNested(i, 'address', { zip: e.target.value })} /></Field>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Field label="Year built"><Input value={loc.building.year_built} onChange={(e) => setLocNested(i, 'building', { year_built: e.target.value.replace(/\D/g, '').slice(0, 4) })} /></Field>
                <Field label="Area (sq ft)"><Input value={loc.building.area_sqft} onChange={(e) => setLocNested(i, 'building', { area_sqft: e.target.value.replace(/[^0-9]/g, '') })} /></Field>
                <Field label="Protection class"><Input value={loc.building.protection_class} onChange={(e) => setLocNested(i, 'building', { protection_class: e.target.value })} placeholder="e.g. 5" /></Field>
                <Field label="Occupancy"><Input value={loc.building.occupancy} onChange={(e) => setLocNested(i, 'building', { occupancy: e.target.value })} placeholder="e.g. Private Hangar" /></Field>
                <Field label="Construction">
                  <NativeSelect value={loc.building.construction} onChange={(e) => setLocNested(i, 'building', { construction: e.target.value })} options={CONSTRUCTION_OPTS} />
                </Field>
                <Field label="Alarm system">
                  <NativeSelect value={loc.building.alarm_system} onChange={(e) => setLocNested(i, 'building', { alarm_system: e.target.value })} options={ALARM_OPTS} />
                </Field>
                <Field label="Sprinklered">
                  <YesNoButtons value={loc.building.sprinklered} onChange={(v) => setLocNested(i, 'building', { sprinklered: v })} />
                </Field>
              </div>
              <div>
                <span className="mb-1 block text-[11px] text-text-dim">Updates · year of last (if over 25 yrs)</span>
                <div className="grid grid-cols-4 gap-2">
                  {UPDATE_TRADES.map(([trade, label]) => (
                    <Field key={trade} label={label}>
                      <Input value={loc.building.updates[trade]} onChange={(e) => setLocNested(i, 'building', { updates: { ...loc.building.updates, [trade]: e.target.value.replace(/\D/g, '').slice(0, 4) } })} placeholder="YYYY" />
                    </Field>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {RISK_FIELDS.map(([k, label]) => (
                  <Field key={k} label={label}>
                    <YesNoButtons value={loc.risk[k]} onChange={(v) => setLocNested(i, 'risk', { [k]: v })} />
                  </Field>
                ))}
              </div>
              <div>
                <span className="mb-1 block text-[11px] text-text-dim">Coverage limits requested ($)</span>
                <div className="grid grid-cols-3 gap-2">
                  {COVERAGE_KEYS.map(([k, label]) => (
                    <Field key={k} label={label}>
                      <Input value={loc.coverages[k]} onChange={(e) => setLocNested(i, 'coverages', { [k]: e.target.value.replace(/[^0-9.]/g, '') })} placeholder="0" />
                    </Field>
                  ))}
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <Field label="Misc coverage description"><Input value={loc.coverages.misc_coverage_description} onChange={(e) => setLocNested(i, 'coverages', { misc_coverage_description: e.target.value })} /></Field>
                  <Field label="Misc coverage limit ($)"><Input value={loc.coverages.misc_coverage_limit} onChange={(e) => setLocNested(i, 'coverages', { misc_coverage_limit: e.target.value.replace(/[^0-9.]/g, '') })} /></Field>
                </div>
              </div>
            </div>
          ))}

          {/* Broker-added building/location (schedule growth after intake,
              e.g. a carrier revision folding another hangar into the policy).
              Persists as overlay added_locations; the client intake stays
              frozen and per-row removal only exists on added rows. */}
          <Button type="button" variant="outline" size="sm" onClick={addLocation}>
            + Add location / building
          </Button>

          {/* Requested premises liability */}
          <div className="space-y-2">
            <SectionTitle>Requested Premises Liability · per occurrence / aggregate ($000s)</SectionTitle>
            <div className="flex flex-wrap gap-2">
              {LIABILITY_TIERS.map(([id, label]) => (
                <Button
                  key={id}
                  type="button"
                  variant={form.liability.tier === id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setForm((f) => ({
                    ...f,
                    liability: f.liability.tier === id
                      ? { tier: '', other_per_occurrence: '', other_aggregate: '' }
                      : { ...f.liability, tier: id },
                  }))}
                >
                  {label}
                </Button>
              ))}
            </div>
            {form.liability.tier === 'other' && (
              <div className="grid grid-cols-2 gap-2">
                <Field label="Per occurrence ($)"><Input value={form.liability.other_per_occurrence} onChange={(e) => setForm((f) => ({ ...f, liability: { ...f.liability, other_per_occurrence: e.target.value.replace(/[^0-9.]/g, '') } }))} /></Field>
                <Field label="Aggregate ($)"><Input value={form.liability.other_aggregate} onChange={(e) => setForm((f) => ({ ...f, liability: { ...f.liability, other_aggregate: e.target.value.replace(/[^0-9.]/g, '') } }))} /></Field>
              </div>
            )}
            <p className="text-[11px] text-text-dim">This is the REQUESTED limit. A carrier&apos;s quoted tier (Quoted terms sheet) overrides it on the packet.</p>
          </div>

          {/* History */}
          <div className="space-y-2">
            <SectionTitle>Prior Insurance &amp; Underwriting</SectionTitle>
            <div className="grid grid-cols-3 gap-2">
              <Field label="Prior coverage in force?">
                <YesNoButtons value={form.history.prior_coverage} onChange={(v) => setHistory('prior_coverage', v)} />
              </Field>
              <Field label="Losses / claims (5 yr)?">
                <YesNoButtons value={form.history.losses_5yr} onChange={(v) => setHistory('losses_5yr', v)} />
              </Field>
              <Field label="Bankruptcies / liens (5 yr)?">
                <YesNoButtons value={form.history.bankruptcies} onChange={(v) => setHistory('bankruptcies', v)} />
              </Field>
            </div>
            <Field label="Loss detail">
              <textarea
                rows={3}
                value={form.history.losses_detail}
                onChange={(e) => setHistory('losses_detail', e.target.value)}
                className="w-full rounded-card border border-border bg-surface px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-accent/40"
              />
            </Field>
          </div>

          {/* Comments */}
          <div className="space-y-2">
            <SectionTitle>Comments (printed on the application)</SectionTitle>
            <textarea
              rows={3}
              value={form.comments}
              onChange={(e) => setForm((f) => ({ ...f, comments: e.target.value }))}
              className="w-full rounded-card border border-border bg-surface px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-accent/40"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border pt-3">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                Saving…
              </>
            ) : (
              'Save & regenerate'
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
